import {
  ActivityType,
  ActivityTypeLabels,
  CallOutcome,
  CallOutcomeLabels,
  EscalationStatus,
  PolicyEscalationType,
} from "shared";
import { When } from "react-if";
import { useEffect, useState } from "react";
import { PencilSquareIcon, PhoneIcon } from "@heroicons/react/24/outline";

import { Notes } from "../../Molecules/Notes";
import { useAddNoteMutation } from "../../../services/api/escalationApi/escalation";
import { useCreateContactActivityMutation, useFetchContactActivitiesQuery } from "../../../services/api/contactsApi/contact";
import { Select } from "../../../../common/Atoms/Select";
import { TextArea } from "../../../../common/Atoms/TextArea";
import { DateTimeInput, nowDateTimeLocal } from "../../../../common/Atoms/DateTimeInput";
import { Button } from "../../../../common/Atoms/Button";
import { useCurrentEscalation } from "../../../hooks/useCurrentEscalation";

const activityTypeOptions = Object.values(ActivityType).map(value => ({
  label: ActivityTypeLabels[value],
  value,
}));

const callOutcomeOptions = Object.values(CallOutcome).map(value => ({
  label: CallOutcomeLabels[value],
  value,
}));

/** `escalationId` overrides the route param, e.g. when rendered in the notes drawer */
export function EscalationNotes({ escalationId }: { escalationId?: number } = {}) {
  const { currentEscalation: escalation } = useCurrentEscalation(escalationId);
  const [addNoteApiCall, { isLoading: addNoteLoading }] = useAddNoteMutation();
  const [createActivity, { isLoading: createActivityLoading }] = useCreateContactActivityMutation();
  const [ composerMode, setComposerMode ] = useState<`note` | `activity` | null>(null);
  const [ activityType, setActivityType ] = useState<ActivityType>(ActivityType.PHONE_CALL);
  const [ callOutcome, setCallOutcome ] = useState<CallOutcome | null>(null);
  const [ activityNote, setActivityNote ] = useState(``);
  const [ activityTime, setActivityTime ] = useState(nowDateTimeLocal());

  const contactId = escalation?.contact?.id;
  const isOpenCall = escalation?.escalationType === PolicyEscalationType.CALL
    && escalation?.escalationStatus === EscalationStatus.PENDING;

  // The router reuses this component across escalations, so start each one with a fresh draft.
  // On the escalation page, an open call escalation starts with the activity composer open.
  useEffect(() => {
    setActivityNote(``);
    setActivityType(ActivityType.PHONE_CALL);
    setCallOutcome(null);
    setActivityTime(nowDateTimeLocal());
    setComposerMode(!escalationId && isOpenCall ? `activity` : null);
  }, [escalation?.id]);

  const { data: activitiesData } = useFetchContactActivitiesQuery({
    organisationId: escalation?.organisationId,
    contactId,
  }, {
    skip: !escalation || !contactId,
  });

  async function addNote(content: string, mentionedUsers: string[], attachments: File[], syncAttachments: boolean) {
    if (!escalation) return;

    await addNoteApiCall({
      organisationId: escalation.organisationId,
      escalationId: escalation.id,
      note: content,
      mentionedUsers,
      attachments,
      syncAttachments,
    });

    setComposerMode(null);
  }

  async function saveActivity() {
    if (!escalation || !contactId) return;

    await createActivity({
      organisationId: escalation.organisationId,
      contactId,
      type: activityType,
      callOutcome: activityType === ActivityType.PHONE_CALL ? callOutcome || undefined : undefined,
      note: activityNote.trim() || undefined,
      escalationId: escalation.id,
      activityTime: activityTime ? new Date(activityTime).toISOString() : undefined,
    }).unwrap();

    setActivityNote(``);
    setActivityType(ActivityType.PHONE_CALL);
    setCallOutcome(null);
    setActivityTime(nowDateTimeLocal());
    setComposerMode(null);
  }

  if (!escalation) {
    return null;
  }

  return (
    <div>
      { /* CTAs to pick what to add */ }
      <When condition={ composerMode !== `note` }>
        <div className={ `flex gap-2 mb-3` }>
          <When condition={ composerMode === null }>
            <Button
              color={ `paidniceGreenOutline` }
              size={ `sm` }
              onClick={ () => {
                // The page can sit open for a while, so start from the time the composer opened
                setActivityTime(nowDateTimeLocal());
                setComposerMode(`activity`);
              } }
            >
              <PhoneIcon className={ `h-4 w-4 mr-1 inline` } />
              { `Record Activity` }
            </Button>
          </When>
          <Button
            color={ `paidniceGreenOutline` }
            size={ `sm` }
            onClick={ () => setComposerMode(`note`) }
          >
            <PencilSquareIcon className={ `h-4 w-4 mr-1 inline` } />
            { `Write Note` }
          </Button>
        </div>
      </When>

      { /* Activity composer */ }
      <When condition={ composerMode === `activity` }>
        <div className={ `border border-gray-200 rounded-md mb-3 p-3 space-y-3` }>
          <Select
            label={ `Activity Type` }
            name={ `activityType` }
            options={ activityTypeOptions }
            selected={ activityType }
            onChange={ ({ value }) => setActivityType(value as ActivityType) }
          />

          <When condition={ activityType === ActivityType.PHONE_CALL }>
            <Select
              label={ `Call Outcome` }
              name={ `callOutcome` }
              emptyText={ `Optionally, select a call outcome...` }
              options={ callOutcomeOptions }
              selected={ callOutcome }
              onChange={ ({ value }) => setCallOutcome((value as CallOutcome) || null) }
              nullable
            />
          </When>

          <DateTimeInput
            label={ `When` }
            name={ `activityTime` }
            value={ activityTime }
            onChange={ ({ value }) => setActivityTime(value) }
          />

          <TextArea
            label={ `Note` }
            name={ `activityNote` }
            value={ activityNote }
            onChange={ ({ value }) => setActivityNote(String(value)) }
            placeholder={ `Optional note about this activity` }
          />

          <div className={ `flex justify-end gap-2` }>
            <Button
              color={ `gray` }
              size={ `sm` }
              onClick={ () => setComposerMode(null) }
            >
              { `Cancel` }
            </Button>
            <Button
              color={ `paidniceGreenOutline` }
              size={ `sm` }
              onClick={ saveActivity }
              loading={ createActivityLoading }
              disabled={ createActivityLoading }
            >
              { `Record Activity` }
            </Button>
          </div>
        </div>
      </When>

      <Notes
        notes={ escalation?.notes }
        activities={ activitiesData?.activities }
        loading={ addNoteLoading }
        enableNewNote={ composerMode === `note` }
        onSave={ addNote }
        onCancel={ () => setComposerMode(null) }
      />
    </div>
  );
}
